<template>
  <nav class="desktop-navigation">
    <div class="skip-navigation" ref="skipNavigation">
      <a
        class="skip-navigation__link"
        href="#site-content"
        tabindex="1"
        @focus="toggleSkipNavigation"
        @blur="toggleSkipNavigation(true)"
        >{{ $t('navigation.skip_navigation') }}
      </a>
    </div>

    <transition name="fade-nav-overlay">
      <div
        v-show="overlayIsShown"
        class="desktop-navigation__overlay-background"
        @click=";[setAdditionalNav(''), setOverlayState]"
      ></div>
    </transition>
    <div
      class="main-navigation"
      :style="{ paddingRight: scrollbarEqualizerMargin }"
      @click="setAdditionalNav('')"
    >
      <!-- Logo -->
      <NavLogo :homepageUrl="homepageUrl"></NavLogo>
      <!-- Main links -->
      <div class="main-links" role="menubar" :aria-label="$t('navigation.main_links')">
        <a
          v-for="(link, index) in $store.state.nav.topics"
          ref="mainLink"
          :key="index"
          :href="link.href"
          class="main-links__link"
          :aria-haspopup="link.blocks.length > 0 ? 'true' : 'false'"
          :aria-exanded="subNavIsOpen && currentTopic === index ? 'true' : 'false'"
          @mouseenter="onMainLinkMouseenter(link, index)"
          @mouseleave="onMainLinkLeave"
          @focus="link.blocks.length ? openSubnav(index, link.href) : closeSubnav()"
          @click.prevent="onMainLinkClick(link.href)"
          @keydown="onMainLinkKeydown($event, index, link)"
        >
          <div
            class="main-links__link-copy"
            :class="currentTopic === index && subNavIsOpen ? 'is-active' : ''"
          >
            {{ link.title }}
          </div>
        </a>
      </div>
      <!-- Additional main links -->
      <AdditionalMainLinks
        :links="additionalMainLinks"
        @closeSubnav="closeSubnav"
        @registerSubnavKeyListener="registerSubnavKeyListener"
      ></AdditionalMainLinks>
    </div>

    <!-- Subnavigation -->
    <Subnavigation
      v-if="hasPageLoaded"
      :blocks="subNavBlocks"
      :isTransitioning="subNavIsTransitioning"
      :isWideDesktop="wideDesktopViewport"
      @subnav-close="closeSubnav()"
      @subnav-open="openSubnav(currentTopic, undefined, false)"
      @subnav-click="handleSubnavClick($event)"
      @set-block="setCurrentBlock($event)"
      @teaser-click="trackTeaserClick"
      @transition-end="subNavIsTransitioning = false"
    />

    <!-- Additional navigation -->
    <div class="additional-nav">
      <AccountNav v-if="shouldRenderAccountNav" />
      <Search v-if="hasPageLoaded" />
      <MiniCart v-if="hasPageLoaded" />
    </div>
  </nav>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import accountStore from '@/stores/account'
import { getPathFromUrl } from '@/helpers/url'
import { registerStores } from '@/helpers/stores'
import { KEY_TAB, KEY_ARROW_DOWN } from '@/helpers/keyCodes'
import NavLogo from '@/components/Navigation/NavLogo/NavLogo.vue'
import AdditionalMainLinks from '@/components/Navigation/AdditionalMainLinks/AdditionalMainLinks.vue'
import AccountNav from '@/components/Navigation/AccountNav/AccountNav.vue'
import {
  navSettings,
  handleFilterToFilterPageNavigation,
} from '@/components/Navigation/navigation-helpers'

const Debouncer = On.Debouncer()

export default {
  name: 'DesktopNav',
  components: {
    AccountNav,
    AdditionalMainLinks,
    NavLogo,
    MiniCart: () => import('@/components/Navigation/MiniCart/MiniCart.vue'),
    Search: () => import('@/components/Search/Search.vue'),
    Subnavigation: () => import('@/components/Navigation/Subnavigation.vue'),
  },

  props: {
    hasPageLoaded: Boolean,
  },

  data() {
    return {
      scrollPosition: 0,
      subNavIsTransitioning: false,
      wideDesktopViewport: window.innerWidth > 1400,
    }
  },

  computed: {
    ...mapState('nav', ['navigationVariant']),
    ...mapGetters({
      additionalMainLinks: 'nav/additionalMainLinks',
      currentTopic: 'nav/currentTopic',
      currentAdditionalNav: 'nav/currentAdditionalNav',
      dropdownNavIsBlocked: 'nav/dropdownNavIsBlocked',
      shouldAvoidBlockToggling: 'nav/shouldAvoidBlockToggling',
      homepageUrl: 'page/homepageUrl',
      orderIsInitialized: 'order/dataIsInitialized',
      overlayIsOpen: 'nav/overlayIsOpen',
      subNavIsOpen: 'nav/subNavIsOpen',
      scrollbarWidth: 'page/scrollbarWidth',
      scrollbarEqualizerMargin: 'page/scrollbarEqualizerMargin',
      topics: 'nav/topics',
      vpWidth: 'viewport/width',
      hasFetchedCurrentUser: 'account/hasFetchedCurrentUser',
      locale: 'page/locale',
    }),

    subNavBlocks() {
      return this.topic.blocks
    },

    topic() {
      const emptyTopic = { teaser: {}, blocks: [] }
      return this.topics[this.currentTopic] || emptyTopic
    },

    shouldRenderAccountNav() {
      const controller = document.body.getAttribute('data-controller')

      return (
        this.hasFetchedCurrentUser &&
        controller != 'user_sessions' &&
        controller != 'user_registrations' &&
        controller != 'user_passwords'
      )
    },

    overlayIsShown() {
      return (
        (this.overlayIsOpen ||
          this.currentAdditionalNav === 'mini-cart' ||
          this.currentAdditionalNav === 'search') &&
        this.orderIsInitialized
      )
    },
  },

  watch: {
    currentAdditionalNav() {
      if (this.currentAdditionalNav !== 'mini-cart' && this.currentAdditionalNav !== 'search') {
        this.setOverlayState(false)
      }
    },

    overlayIsOpen: 'setScrollbarEqualizers',
    vpWidth: 'handleResize',
  },

  beforeCreate() {
    registerStores({ account: accountStore })
  },

  mounted() {
    this.$store.dispatch('account/fetchData')
    // In case a user hovers over the navigation on initial load,
    // the dropdownNav (subnav and additional nav) should not open.
    // To prevent opening, dropdownNav is blocked (from opening) by default.
    // We don't unblock dropdownNav as long as the cursor is inside a main link.
    this.addDropdownNavBlockToggleCondition(e =>
      e.target.parentNode.contains(document.querySelector('.main-links__link'))
    )
    this.addDropdownNavBlockToggleCondition(e =>
      e.target.parentNode.contains(document.querySelector('.additional-main-links__link'))
    )
    document.body.addEventListener('mouseover', this.conditinallyUnblockDropdownNav)
    window.addEventListener('beforeUnload', function (event) {
      event.preventDefault()
    })
  },

  methods: {
    ...mapActions({
      setAdditionalNav: 'nav/setAdditionalNav',
      toggleSubnav: 'nav/toggleSubnav',
      toggleDocumentOverflow: 'page/toggleDocumentOverflow',
    }),

    ...mapMutations({
      blockDropdownNav: 'nav/blockDropdownNav',
      setCurrentBlock: 'nav/setCurrentBlock',
      setCurrentTopic: 'nav/setCurrentTopic',
      setOverlayState: 'nav/setOverlayState',
      unblockDropdownNav: 'nav/unblockDropdownNav',
      addDropdownNavBlockToggleCondition: 'nav/addDropdownNavBlockToggleCondition',
    }),

    handleSubnavClick(e) {
      this.closeSubnav()
      let href = ''
      const isTeaserLink = e.target.classList.contains(navSettings.teaserLinkClass)
      if (e.target.href) href = e.target.href
      else if (e.target.parentNode.href) href = e.target.parentNode.href
      handleFilterToFilterPageNavigation(href)

      if (href !== '' && !isTeaserLink) {
        this.trackNavigation('sub_navigation')
      }
    },

    conditinallyUnblockDropdownNav(e) {
      if (!this.shouldAvoidBlockToggling(e)) {
        this.unblockDropdownNav()
        document.body.removeEventListener('mouseover', this.conditinallyUnblockDropdownNav)
      }
    },

    handleResize() {
      this.setAdditionalNav('')
      let self = this
      Debouncer.debounce({
        debounceTime: 100,
        callbackArgument: self,
        callback: function () {
          return (self.wideDesktopViewport = window.innerWidth > 1400)
        },
      })
    },

    setScrollbarEqualizers() {
      this.toggleDocumentOverflow(!this.overlayIsOpen)

      const helpButton = document.querySelector('.helpButton')
      if (helpButton) {
        helpButton.style.right = this.overlayIsOpen ? `${this.scrollbarWidth + 12}px` : ''
      }
    },

    onMainLinkMouseenter(link, index) {
      this.subNavIsTransitioning = false
      if (link.href === this.currentLinkHref) this.isClosingSubnavAllowed = false
      this.currentLinkHref = link.href
      link.blocks.length ? this.openSubnav(index, link.href) : this.toggleSubnav(false)
      this.setCurrentBlock(0)
      this.setAdditionalNav('')
    },

    onMainLinkLeave() {
      this.isOpeningSubnavAllowed = false
      this.unblockDropdownNav()
    },

    onMainLinkClick(href) {
      this.closeSubnav()
      this.blockDropdownNav()
      handleFilterToFilterPageNavigation(href)
      this.trackNavigation('main_navigation')
    },

    onMainLinkKeydown($event, index, link) {
      this.jumpToSubnav($event)
      this.openSubnav(index, link.href)
    },

    toggleSkipNavigation(closeSubnav) {
      const self = this
      this.$nextTick(function () {
        self.$refs.skipNavigation.classList.toggle('has-focus')
      })
      if (closeSubnav) this.closeSubnav()
    },

    closeSubnav(el) {
      if (el !== undefined && !el.currentTarget.hasAttribute('href')) return
      this.isClosingSubnavAllowed = true
      setTimeout(() => {
        if (this.isClosingSubnavAllowed) {
          this.toggleSubnav(false)
        }
      }, navSettings.subnavOpenDelay)
    },

    openSubnav(currentTopic, href, isOpeningDelayed = true) {
      // If we change the topic, a transition on the subnav content is triggered
      // ...which is gonna be canceled on transitionend
      if (isOpeningDelayed) {
        this.isOpeningSubnavAllowed = true
        setTimeout(() => {
          if (this.isOpeningSubnavAllowed) {
            this.setSubnavState(currentTopic)
          }
        }, navSettings.subnavOpenDelay)
      } else {
        this.setSubnavState(currentTopic)
      }
      if (!href) return

      // Track promotion view in case subnav is still
      // ...open after the subnavOpenDelay time has passed
      setTimeout(() => {
        if (this.currentTopic === currentTopic && this.subNavIsOpen) {
          this.$notifier.trackEvent({
            action: this.$notifier.ACTIONS.promotionView,
            promotions: this.getPromotionsForTracking(),
          })
        }
      }, navSettings.subnavOpenDelay)
    },

    setSubnavState(currentTopic) {
      if (this.currentTopic !== currentTopic) this.subNavIsTransitioning = true
      if (currentTopic >= 0) this.setCurrentTopic(currentTopic)
      if (!this.dropdownNavIsBlocked) this.toggleSubnav(true)
    },

    trackNavigation(place) {
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.navigation,
        details: {
          action: place,
          label: getPathFromUrl(window.location.href),
        },
      })
    },

    trackTeaserClick() {
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.promotionClick,
        promotions: this.getPromotionsForTracking(),
      })
    },

    getPromotionsForTracking() {
      return [
        {
          name: this.topic.teaser.ctaText,
          creative: this.topic.teaser.imageUrl,
          position: this.$CONTEXTS.navigationTeaser,
        },
      ]
    },

    jumpToSubnav($event) {
      if ($event.keyCode === KEY_ARROW_DOWN) {
        // With arrow down key, jump to first link in subnav
        this.$refs.blocktitle[0].focus()
        this.subNavHasFocus = true
        this.registerSubnavKeyListener()
      }
    },

    findNextTabStop(el) {
      const potentialTabStops = [
        ...document.querySelectorAll('input, button, select, textarea, a[href]'),
      ]
      const tabStops = potentialTabStops.filter(stop => stop.tabIndex >= '0')
      const index = tabStops.indexOf(el)
      return tabStops[index + 1] || tabStops[0]
    },

    registerSubnavKeyListener() {
      window.addEventListener('keydown', this.checkSubNavLeave)
    },

    // Accessibilty handling
    checkSubNavLeave(event) {
      // Checks on tab if the current stop was the last possible tabstop inside subnav
      // If true finds the element which shall be focussable next outside subnav
      if (event.keyCode === KEY_TAB) {
        const nextTabStop = this.findNextTabStop(document.activeElement)
        const nextTabStopIsInsideHeader = nextTabStop.closest('header') !== null
        if (!nextTabStopIsInsideHeader) {
          event.preventDefault()
          const nextFocussableLink =
            document.activeElement.closest('.sub-navigation__main-content') !== null
              ? this.$refs.mainLink[this.currentTopic + 1]
              : document.querySelector('.trigger-' + this.$state.store.currentAdditionalNav)
                  .nextSibling
          nextFocussableLink !== null
            ? nextFocussableLink.focus()
            : document.querySelector('.site-content').focus()
          window.removeEventListener('keydown', this.checkSubNavLeave)
        }
      }
    },
  },
}
</script>
